import mapboxgl, { Map } from "mapbox-gl";

import Walker, { DisplayWalker } from "./Walker.ts";
import { UpdatePois, DisplayRoutePOIs } from "./Pois.ts";
import { UpdateBackgroundSounds, DisplayBackgroundSounds } from "./Bss.ts";
import { UpdateFocusedSounds, DisplayFocusedSounds } from "./Fss.ts";
import { UpdateRouteLine, DisplayRouteLine, UpdateRouteLineVertices } from "./Routeline.ts";
import { CenterExtents } from "./Mappanel.part.ts";
import { DisplaySelectedSound } from "./Selection.ts";
import { RouteState } from "../../../api/edits/editRoute.responses.ts";
import { ToolType } from "../../../Reducer.ts";

// refreshing map on route change
// pre:   - map has current map
//        - statRoute has current route
//        - myWalker has walker object
// post:  selection layer on map is updated
export function EffectRouteChange(
  map: Map,
  stateRoute: RouteState,
  myWalker: Walker,
  currentLanguage: string,
  currentTool: ToolType
): void {
  UpdatePois(map, stateRoute);
  UpdateBackgroundSounds(map, stateRoute);
  UpdateFocusedSounds(map, stateRoute);
  UpdateRouteLine(map, stateRoute);
  UpdateRouteLineVertices(map, stateRoute, currentTool === "draw");

  // initialize AudioSources
  myWalker.RemoveAudioSources();
  myWalker.InitializeAudioSources(stateRoute, currentLanguage);
}

// refreshing map on route change
// pre:   - stateTool is currently selected tool
//        - myWalker has walker object
//        - statRoute has current route
//        - requestRef has current function for requestin nex animation frame
//        - animate is animation function
//        - map has current map
//        - setSelected is function for selecting object
// post:  tool for simulation is updated
export function EffectSimulationStates(
  stateTool: string,
  myWalker: Walker,
  stateRoute: RouteState,
  requestRef: number,
  animate: FrameRequestCallback,
  map: Map,
  setSelected: React.Dispatch<React.SetStateAction<string>>,
  currentLanguage: string
): void {
  if (stateTool === "start") {
    // initialize AudioSources
    myWalker.InitializeAudioSources(stateRoute, currentLanguage);
    myWalker.Start();
    requestRef = requestAnimationFrame(animate);
  } else if (stateTool === "stop") {
    myWalker.HideWalker(map);
    myWalker.RemoveAudioSources();
    myWalker.Stop();
    cancelAnimationFrame(requestRef);
  } else if (stateTool === "move") {
    setSelected("rprop");
  }
}

// map initialization
// pre:   - map has current map, and it is pased by REFERENCE as OBJECT
//        - cen is the current center of the map
//        - stateRoute has current route
//        - myWalker has walker object
//        - mapContainer is HTML element containing the map
//        - zoom is the current state of the zoom of the map
//        - setLng, setLat, setZoom state set functions for lng, lat and zoom
// post:  map is initialized

export function EffectMapInitialization(
  map: { value: Map | null },
  center: [number, number],
  stateRoute: RouteState,
  myWalker: Walker,
  mapContainer: HTMLElement,
  zoom: number,
  setCenter: React.Dispatch<React.SetStateAction<[number, number]>>,
  setZoom: React.Dispatch<React.SetStateAction<number>>
): void {
  // initialize map only once!
  if (map.value) {
    return;
  } else {
    // center the map to the extents of the current route
    CenterExtents(center, stateRoute);
    // set walker to center of the map
    myWalker.SetLngLat(center[0], center[1]);
    // loading of the map
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || "";

    map.value = new mapboxgl.Map({
      container: mapContainer,
      projection: "mercator",
      style: "mapbox://styles/mapbox/dark-v11",
      center: center,
      zoom: zoom,
    });

    // add zoom and rotation controls to the map.
    map.value.addControl(new mapboxgl.NavigationControl());
    // Disable box-zoom so Shift+click does not trigger area selection
    map.value.boxZoom.disable();
    // set cursor to arrow
    map.value.getCanvas().style.cursor = "default";
    // mouse events handler

    // display layers
    DisplayBackgroundSounds(map.value, stateRoute);
    DisplayFocusedSounds(map.value, stateRoute);
    DisplayRouteLine(map.value, stateRoute);
    DisplayRoutePOIs(map.value, stateRoute);
    DisplaySelectedSound(map.value, stateRoute);
    DisplayWalker(map.value);
  }
}
